import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import type {
  HeatmapColorScale,
  HeatmapWeeks,
} from '@/components/orbitoneHeatmapWidget/orbitoneHeatmapWidget.constants.ts';
import type { OrbitoneHeatmapProviderType } from '@/components/orbitoneHeatmapWidget/orbitoneHeatmapWidget.types.ts';

/**
 * Persists only the user's selections per widget: the provider, the week count and the colour
 * scale.
 * id/name come from layout storage or configInput — this store does not shadow them.
 */
interface OrbitoneHeatmapConfigStore {
  providerByWidgetId: Record<string, OrbitoneHeatmapProviderType>;
  weeksByWidgetId: Record<string, HeatmapWeeks>;
  colorScaleByWidgetId: Record<string, HeatmapColorScale>;
  setProvider: (widgetId: string, provider: OrbitoneHeatmapProviderType) => void;
  setWeeks: (widgetId: string, weeks: HeatmapWeeks) => void;
  setColorScale: (widgetId: string, colorScale: HeatmapColorScale) => void;
}

const useOrbitoneHeatmapConfigZustandStore = create(
  persist<OrbitoneHeatmapConfigStore>(
    set => ({
      providerByWidgetId: {},
      weeksByWidgetId: {},
      colorScaleByWidgetId: {},
      setProvider: (widgetId, provider) =>
        set(state => {
          const previousProvider = state.providerByWidgetId[widgetId];

          // Skip no-op updates so subscribers are not notified with a new reference.
          if (previousProvider === provider) {
            return state;
          }

          return {
            providerByWidgetId: {
              ...state.providerByWidgetId,
              [widgetId]: provider,
            },
          };
        }),
      setWeeks: (widgetId, weeks) =>
        set(state => {
          // Skip no-op updates so subscribers are not notified with a new reference.
          if (state.weeksByWidgetId[widgetId] === weeks) {
            return state;
          }

          return {
            weeksByWidgetId: {
              ...state.weeksByWidgetId,
              [widgetId]: weeks,
            },
          };
        }),
      setColorScale: (widgetId, colorScale) =>
        set(state => {
          // Skip no-op updates so subscribers are not notified with a new reference.
          if (state.colorScaleByWidgetId[widgetId] === colorScale) {
            return state;
          }

          return {
            colorScaleByWidgetId: {
              ...state.colorScaleByWidgetId,
              [widgetId]: colorScale,
            },
          };
        }),
    }),
    {
      name: 'orbitone-heatmap-config',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export const useOrbitoneHeatmapConfigStorage = (widgetId: string) => {
  const { storedProvider, storedWeeks, storedColorScale, setProvider, setWeeks, setColorScale } =
    useOrbitoneHeatmapConfigZustandStore(
      useShallow(s => ({
        storedProvider: s.providerByWidgetId[widgetId],
        storedWeeks: s.weeksByWidgetId[widgetId],
        storedColorScale: s.colorScaleByWidgetId[widgetId],
        setProvider: s.setProvider,
        setWeeks: s.setWeeks,
        setColorScale: s.setColorScale,
      }))
    );

  return {
    provider: storedProvider,
    weeks: storedWeeks,
    colorScale: storedColorScale,
    setProvider: (provider: OrbitoneHeatmapProviderType) => setProvider(widgetId, provider),
    setWeeks: (weeks: HeatmapWeeks) => setWeeks(widgetId, weeks),
    setColorScale: (colorScale: HeatmapColorScale) => setColorScale(widgetId, colorScale),
  };
};
